import * as React from 'react';

import { useQuery } from '@apollo/client';
import { getSourceEventParams, useAmplitude } from '@crehana/analytics';
import { useTranslation } from '@crehana/i18n';
import ArrowRight from '@crehana/icons/outline/ArrowRight';
import {
  Col,
  Container,
  InverseButton,
  PrimaryButton,
  Row,
  Slider,
  // biome-ignore lint/style/noRestrictedImports: legacy code - style/noRestrictedImports
} from '@crehana/ui';
import { notEmpty } from '@crehana/utils';

import CAMPAIGN_LANDING_MEMBERSHIP from '@/views/Campaign/graphql/CampaignLandingMembership.graphql';
import type {
  CampaignLandingMembership,
  CampaignLandingMembership_membershipTypesV2_edges_node as CampaignLandingMembershipNode,
  CampaignLandingMembershipVariables,
} from '@/views/Campaign/graphql/types/CampaignLandingMembership';
import type { CampaignProps } from '@/views/Campaign/types';

import { MEMBERSHIP_EXTRA_DATA } from './constants';
import Loader from './Loader';
import {
  PremiumCardWrapper,
  RightContent,
  SliderWrapper,
  Wrapper,
} from './Premium.styles';
import type { MembershipCard, SupportedMembershipsEnum } from './types';

const completeData = (node: CampaignLandingMembershipNode): MembershipCard => {
  const tag = node.tag as SupportedMembershipsEnum;

  return {
    ...node,
    ...MEMBERSHIP_EXTRA_DATA[tag],
  } as MembershipCard;
};

const reorderMembership = (
  formattedData: MembershipCard[],
): MembershipCard[] => {
  // Temporal hack to show first the highlighted membership, we dont have this data from backend yet.
  const DUO_INDEX = formattedData.findIndex(
    item => (item.tag as SupportedMembershipsEnum) === 'PREMIUM_DUO',
  );
  const duo = formattedData[DUO_INDEX];

  formattedData.splice(DUO_INDEX, 1);
  formattedData.unshift(duo);
  return formattedData;
};

const Premium: React.FC<React.PropsWithChildren<CampaignProps['plans']>> = ({
  backgroundDesktop,
  backgroundMobile,
  callToAction,
  callToActionUrl,
  title,
  description,
  cardCallToAction,
  cardCallToActionUrl,
}) => {
  const { t } = useTranslation();
  const [membership, setMembership] = React.useState<MembershipCard[]>([]);
  const { logEvent } = useAmplitude();

  const { loading } = useQuery<
    CampaignLandingMembership,
    CampaignLandingMembershipVariables
  >(CAMPAIGN_LANDING_MEMBERSHIP, {
    variables: { group: 'LANDING_CASE_B' },
    onCompleted(data) {
      if (data && data.membershipTypesV2?.edges.length) {
        let formattedData = data.membershipTypesV2.edges
          .map(item => (item?.node ? completeData(item?.node) : null))
          .filter(notEmpty);

        formattedData = reorderMembership(formattedData);

        setMembership(formattedData);
      }
    },
  });

  const handleSectionCtaClick = () => {
    logEvent({
      event: 'Click Premium CTA',
      data: {
        ...getSourceEventParams(),
        'CTA Url': callToActionUrl,
        'CTA Label': callToAction,
      },
    });
    return true;
  };

  const handleCardCtaClick = ({
    membership,
  }: {
    membership: MembershipCard;
  }) => {
    logEvent({
      event: 'Click Premium Card CTA',
      data: {
        ...getSourceEventParams(),
        'Clicked Membership': membership,
      },
    });
    return true;
  };

  if (loading || !membership.length) {
    return <Loader />;
  }

  const TitleCol = () => (
    <RightContent className="text-center m-auto lg:mr-auto lg:ml-0 lg:text-left mb-48 lg:mb-0 lg:h-full lg:flex lg:flex-col lg:justify-center lg:items-start ">
      <h2 className="font-h1 mt-32 lg:mt-0 text-white lg:w-8/12">{title}</h2>
      <p className="font-body2 my-32 text-white lg:w-10/12">{description}</p>
      <InverseButton
        leftIcon={{ component: ArrowRight, size: 20 }}
        isWhite
        as="a"
        size="l"
        href={callToActionUrl}
        label={callToAction}
        onClick={handleSectionCtaClick}
      />
    </RightContent>
  );

  return (
    <Wrapper
      id="premium-section"
      className="lg:flex items-center pb-64 lg:py-0 relative z-2"
      bgDesktop={backgroundDesktop}
      bgMobile={backgroundMobile}
    >
      <Container>
        <Row>
          <Col xs={12} className="lg:hidden">
            <TitleCol />
          </Col>
          <Col lg={6}>
            <SliderWrapper className="m-auto lg:ml-0 mr-auto">
              <Slider
                desktopPaginationSelector=".premium-pagination"
                desktopPaginationType="dots"
                mobilePaginationProps={{
                  type: 'dots',
                }}
                infinite
                // autoplay={{
                //   duration: 6000,
                // }}
                itemsPerPage={{
                  lg: 1,
                  md: 1,
                  sm: 1,
                  xs: 1,
                }}
              >
                {membership.map(item => {
                  return (
                    <div key={`premium-${item.originalId}`}>
                      {item.isHighlighted && (
                        <div className="font-link text-center min-h-32 w-full rounded-10 bg-yellow-main text-base-main mb-8 p-8">
                          ¡Aprovecha la promoción hoy!{' '}
                        </div>
                      )}
                      <PremiumCardWrapper
                        key={item.originalId}
                        className="relative font-link rounded-10 border-solid border-yellow-main bg-base-main-56 p-40 h-full flex flex-col justify-between"
                        isHighlighted={Boolean(item.isHighlighted)}
                      >
                        <div className="text-white">
                          <h3 className="font-h3">{item.title}</h3>
                          <p className="font-body3">{item.numberOfAccounts}</p>
                        </div>
                        <div>
                          <h3 className="font-h3 text-white">{`${item.prices?.currencySymbol}${item.prices?.monthlyPrice}/ mes*`}</h3>
                          <p className="font-body3 text-base-lighter">
                            {`Cobro anual de ${item.prices?.currencySymbol}${item.prices?.total}`}
                          </p>
                        </div>
                        <PrimaryButton
                          as="a"
                          label={cardCallToAction || t('PREMIUM_CTA')}
                          href={cardCallToActionUrl || '/premium'}
                          onClick={() =>
                            handleCardCtaClick({ membership: item })
                          }
                        />
                      </PremiumCardWrapper>
                    </div>
                  );
                })}
              </Slider>
              <div className="premium-pagination w-full flex justify-center mt-24 text-white" />
            </SliderWrapper>
          </Col>
          <Col lg={6} className="hidden lg:block">
            <TitleCol />
          </Col>
        </Row>
      </Container>
    </Wrapper>
  );
};

export default Premium;
